import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { firstValueFrom } from "rxjs";
import { HttpService } from "app/js/src/infrastructure/http/http";
import { IClippingStudioTranscriptRepository } from "app/js/src/domain/clipping-studio/i-clipping-studio-transcript-repository";
import {
  ClippingStudioTranscriptTrack,
  ClippingStudioTranscriptBlock,
} from "app/js/src/domain/clipping-studio/clipping-studio-transcript";
import { ClippingStudioSubtitleEvent } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import {
  parseSrt,
  parseVtt,
  formatSrt,
  formatVtt,
} from "app/js/src/infrastructure/clipping-studio/subtitle-file-format";

/** API response item for transcript track (snake_case from backend). */
interface TranscriptTrackApiItem {
  id: number;
  media_id: number;
  language_code: string;
  transcript_url: string;
  is_original: boolean;
}

/** API response item for subtitle track (snake_case from backend). */
interface SubtitleTrackApiItem {
  id: number;
  media_id: number;
  language_code: string;
  subtitle_url: string;
}

const BASE_PATH = "clipping-studio/transcripts";

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

@Injectable()
export class ClippingStudioTranscriptHttpRepositoryService extends IClippingStudioTranscriptRepository {
  constructor(
    private readonly httpService: HttpService,
    private readonly http: HttpClient,
  ) {
    super();
  }

  async getTranscriptTracks(mediaId: number): Promise<ClippingStudioTranscriptTrack[]> {
    const raw = await this.httpService.get(`${BASE_PATH}/${mediaId}/tracks`);
    const items = (Array.isArray(raw) ? raw : []) as TranscriptTrackApiItem[];
    const tracks: ClippingStudioTranscriptTrack[] = [];
    for (const item of items) {
      const url = item.transcript_url?.trim();
      if (!url) {
        tracks.push({
          language: item.language_code ?? "",
          isOriginal: Boolean(item.is_original),
          blocks: [],
        });
        continue;
      }
      try {
        const content = await firstValueFrom(
          this.http.get(url, { responseType: "text" }),
        );
        const cues = parseSrt(content ?? "");
        const blocks: ClippingStudioTranscriptBlock[] = cues.map((c) => ({
          id: c.index,
          timestamp: formatTimestamp(c.startMs),
          startTimeMs: c.startMs,
          endTimeMs: c.endMs,
          text: c.text,
        }));
        tracks.push({
          language: item.language_code ?? "",
          isOriginal: Boolean(item.is_original),
          blocks,
        });
      } catch {
        tracks.push({
          language: item.language_code ?? "",
          isOriginal: Boolean(item.is_original),
          blocks: [],
        });
      }
    }
    return tracks;
  }

  async getSubtitleTracks(
    mediaId: number,
  ): Promise<Record<string, ClippingStudioSubtitleEvent[]>> {
    const raw = await this.httpService.get(`${BASE_PATH}/${mediaId}/subtitles`);
    const items = (Array.isArray(raw) ? raw : []) as SubtitleTrackApiItem[];
    const result: Record<string, ClippingStudioSubtitleEvent[]> = {};
    for (const item of items) {
      const url = item.subtitle_url?.trim();
      const lang = item.language_code ?? "";
      if (!url) {
        result[lang] = [];
        continue;
      }
      try {
        const content = await firstValueFrom(
          this.http.get(url, { responseType: "text" }),
        );
        const cues = parseSrt(content ?? "");
        result[lang] = cues.map((c) => ({
          id: `sub-${lang}-${c.index}`,
          StartTimeMS: c.startMs,
          EndTimeMS: c.endMs,
          Text: c.text,
          language: lang,
        }));
      } catch {
        result[lang] = [];
      }
    }
    return result;
  }

  async regenerateTranscript(
    _mediaId: number,
    language: string,
  ): Promise<ClippingStudioTranscriptTrack> {
    return { language, isOriginal: true, blocks: [] };
  }

  async updateTranscriptBlock(
    _mediaId: number,
    _language: string,
    _blockId: number,
    _newText: string,
  ): Promise<void> {
    // No backend PATCH yet; client state is updated by manager.
  }

  async generateSubtitlesFromTranscript(
    _mediaId: number,
    _language: string,
  ): Promise<ClippingStudioSubtitleEvent[]> {
    return [];
  }

  async importSubtitles(
    fileContent: string,
    format: "srt" | "vtt",
  ): Promise<ClippingStudioSubtitleEvent[]> {
    const cues =
      format === "vtt"
        ? parseVtt(fileContent)
        : format === "srt"
          ? parseSrt(fileContent)
          : [];
    return cues.map((c, i) => ({
      id: `sub-import-${Date.now()}-${i}`,
      StartTimeMS: c.startMs,
      EndTimeMS: c.endMs,
      Text: c.text,
    }));
  }

  async exportSubtitles(
    subtitles: ClippingStudioSubtitleEvent[],
    format: "srt" | "vtt",
  ): Promise<string> {
    if (!subtitles.length) return "";
    const mapped = subtitles.map((s) => ({
      inMs: s.StartTimeMS,
      outMs: s.EndTimeMS,
      text: s.Text,
    }));
    if (format === "vtt") return formatVtt(mapped);
    if (format === "srt") return formatSrt(mapped);
    return "";
  }
}
